import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Phone, CheckCircle, ArrowRight, Download, FileText,
  Shield, Flame, Zap, Users, Home, AlertTriangle, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LandlordStarterKit = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/leads/landlord`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: 'Landlord Starter Kit Download Request',
          source: 'starter_kit_download'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast({ title: "Success!", description: "Your Starter Kit is ready to download." });
      }
    } catch (error) {
      toast({ title: "Error", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const checklistItems = [
    {
      title: "Energy Performance Certificate (EPC)",
      description: "Must be rated E or above to let legally. Valid for 10 years.",
      icon: Zap,
      urgent: false
    },
    {
      title: "Gas Safety Certificate",
      description: "Annual inspection required by a Gas Safe registered engineer.",
      icon: Flame,
      urgent: true
    },
    {
      title: "Electrical Safety (EICR)",
      description: "Electrical Installation Condition Report required every 5 years.",
      icon: Zap,
      urgent: false
    },
    {
      title: "Right to Rent Checks",
      description: "Verify all adult tenants have the right to rent in the UK.",
      icon: Users,
      urgent: true
    },
    {
      title: "Deposit Protection",
      description: "Protect deposit in a government scheme within 30 days.",
      icon: Shield,
      urgent: true
    },
    {
      title: "Smoke & CO Alarms",
      description: "Working alarms on every floor. CO alarm where solid fuel is used.",
      icon: AlertTriangle,
      urgent: true
    },
    {
      title: "How to Rent Guide",
      description: "Provide the latest government 'How to Rent' guide to tenants.",
      icon: FileText,
      urgent: false
    },
    {
      title: "Licensing Check",
      description: "Check if your property needs an HMO or selective licence.",
      icon: Home,
      urgent: false
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#404040] to-[#2a2a2a] py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-[#404040] mb-2">Your Starter Kit is Ready!</h1>
                <p className="text-gray-600">Here's your Harrow Landlord Compliance Checklist</p>
              </div>

              {/* Checklist Preview */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-[#404040] mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#FF8C42]" />
                  Harrow Landlord Compliance Checklist 2026
                </h2>
                
                <div className="space-y-4">
                  {checklistItems.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.urgent ? 'bg-red-100' : 'bg-[#FF8C42]/10'}`}>
                        <item.icon className={`w-4 h-4 ${item.urgent ? 'text-red-600' : 'text-[#FF8C42]'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#404040]">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-gray-300 flex-shrink-0 ml-auto" />
                    </div>
                  ))}
                </div>

                {/* CTA Section */}
                <div className="mt-6 p-4 bg-[#FF8C42] rounded-lg text-white">
                  <h3 className="font-bold mb-2">Need Help Staying Compliant?</h3>
                  <p className="text-sm text-white/90 mb-3">
                    We handle all compliance requirements for our managed properties. Gas safety, EPCs, electrical checks - all sorted.
                  </p>
                  <Link to="/landlord/free-valuation">
                    <Button className="bg-white text-[#FF8C42] hover:bg-gray-100">
                      Get Free Valuation <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="text-center space-y-4">
                <p className="text-gray-500 text-sm">
                  We've also sent this checklist to your email: {formData.email}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/landlord/services">
                    <Button variant="outline">
                      View Our Services
                    </Button>
                  </Link>
                  <a href="tel:02081898889">
                    <Button className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                      <Phone className="w-4 h-4 mr-2" /> Call Us: 0208 189 8889
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#404040] to-[#2a2a2a]">
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          
          {/* Left - Content */}
          <div className="text-white">
            <div className="inline-block bg-[#FF8C42] text-white text-sm font-semibold px-4 py-1 rounded-full mb-6">
              FREE DOWNLOAD
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              The Harrow Landlord Compliance Checklist 2026
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Stay legal and avoid fines. Everything you need to know about UK landlord compliance in one simple checklist.
            </p>

            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-lg">What's Inside:</h3>
              <div className="grid grid-cols-2 gap-3">
                {['EPC Requirements', 'Gas Safety', 'Electrical Checks', 'Right to Rent', 'Deposit Rules', 'Smoke Alarms', 'HMO Licensing', 'Quick Reference'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF8C42]" />
                    <span className="text-gray-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
              <p className="text-sm text-gray-300">
                <strong className="text-white">Did you know?</strong> Non-compliance can result in fines up to £30,000 and even criminal prosecution. Stay protected with our free checklist.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <Card className="shadow-2xl border-0">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#FF8C42]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Download className="w-8 h-8 text-[#FF8C42]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#404040]">Get Your Free Checklist</h2>
                  <p className="text-gray-500 mt-2">Instant access • No spam</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@example.com"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="07XXX XXXXXX"
                      className="mt-1"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6 text-lg font-semibold"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Download Free Checklist'}
                    <Download className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-center text-xs text-gray-400">
                    We respect your privacy. Unsubscribe anytime.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-[#404040] mb-4">
            Want Us to Handle Compliance For You?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our full management service includes all compliance checks and certifications. Focus on the income, we'll handle the paperwork.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/landlord/free-valuation">
              <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] px-8 py-6">
                Get Free Valuation <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/landlord/services">
              <Button variant="outline" className="px-8 py-6">
                View Our Services
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandlordStarterKit;
